import LegalPage from "@/components/LegalPage";

const sections = [
  {
    title: "Overview",
    paragraphs: [
      "These Terms & Conditions govern the use of BlackRide's website, booking process, chauffeur services, quotes, payments, and guest communications. They are intended to set out the practical terms under which BlackRide provides premium private transport and concierge-led chauffeur arrangements in Saudi Arabia.",
      "By using the website, requesting a quote, or booking a trip with BlackRide, you agree to these Terms & Conditions.",
    ],
  },
  {
    title: "Service Scope",
    paragraphs: [
      "BlackRide provides premium chauffeur and private transfer services including airport transfers, corporate travel, Umrah transport, family transportation, hotel transfers, and intercity journeys. Service availability depends on route, schedule, vehicle class, chauffeur availability, operational conditions, and confirmation by the BlackRide team.",
    ],
  },
  {
    title: "Quotes and Booking Confirmation",
    paragraphs: [
      "A trip request submitted through the website or WhatsApp does not automatically create a confirmed booking. BlackRide may review route details, timing, vehicle requirements, and operational conditions before confirming availability and pricing.",
      "A quote, indicative price, or conversation with our team is not binding until the booking is expressly confirmed by BlackRide. Where applicable, BlackRide may require a deposit or prepayment before the trip is locked into the live operations schedule.",
    ],
  },
  {
    title: "Payments and Deposits",
    paragraphs: [
      "BlackRide may request a deposit, partial prepayment, or full advance payment depending on the route, service type, timing, or booking profile. Payment instructions may be shared by WhatsApp, payment link, bank transfer, invoice, or another approved method.",
      "Bookings that require a deposit may remain unconfirmed until the required amount is received or accepted by BlackRide. Any final balance must be settled in line with the payment terms shared at the time of confirmation.",
    ],
  },
  {
    title: "Changes and Cancellations",
    paragraphs: [
      "Guests should notify BlackRide as early as possible if they need to change pickup time, location, route, or passenger details. Changes may affect pricing, vehicle allocation, or service availability.",
      "Cancellation terms may depend on the notice period, vehicle class, route type, and whether a chauffeur or vehicle has already been assigned. BlackRide may retain or charge reasonable costs in cases of late cancellation, route-specific commitments, no-shows, or already-incurred operational expenses.",
    ],
  },
  {
    title: "Guest Responsibilities",
    paragraphs: [
      "Guests are responsible for providing accurate trip information including date, time, pickup, destination, passenger count, luggage details, and special service notes. Inaccurate or incomplete information may affect service delivery, pricing, or timing.",
    ],
    bullets: [
      "Guests must be ready at the agreed pickup point at the confirmed time.",
      "Guests must ensure luggage and passenger details are suitable for the booked vehicle class.",
      "Guests are responsible for complying with local travel, entry, and documentation requirements.",
      "Guests must treat chauffeurs, vehicles, and staff respectfully and lawfully.",
    ],
  },
  {
    title: "Chauffeur, Vehicle, and Route Operations",
    paragraphs: [
      "BlackRide may substitute a comparable chauffeur or vehicle category where reasonably necessary due to operational, safety, maintenance, traffic, or scheduling requirements. We aim to preserve the confirmed class or a suitable premium alternative whenever possible.",
      "Estimated durations, arrival times, and route plans are indicative and may be affected by traffic, weather, checkpoints, airport conditions, events, road closures, or instructions from authorities. BlackRide will use reasonable efforts to coordinate services professionally and punctually, but cannot guarantee conditions outside its control.",
    ],
  },
  {
    title: "Waiting Time and Additional Charges",
    paragraphs: [
      "Airport and city pickups may include a stated waiting period depending on the service type or confirmed quote. Additional waiting time, route diversions, extra stops, special assistance, oversized luggage handling, tolls, parking, or materially changed itineraries may result in additional charges.",
      "Where practical, BlackRide will communicate additional charges before or during the trip. Some route-based or airport-related charges may only become clear once operational circumstances are confirmed.",
    ],
  },
  {
    title: "Liability",
    paragraphs: [
      "BlackRide will use reasonable care and professional standards in delivering chauffeur services. However, to the fullest extent permitted by law, BlackRide is not liable for indirect losses, missed appointments, missed flights, missed hotel services, or other consequential losses caused by traffic, force majeure, regulatory issues, guest delays, or circumstances beyond reasonable control.",
      "Guests should avoid leaving valuables unattended in vehicles and should ensure they hold any travel insurance they consider necessary.",
    ],
  },
  {
    title: "Website Use and Intellectual Property",
    paragraphs: [
      "All website content, branding, visual assets, service copy, and design elements used by BlackRide remain the property of BlackRide or its licensors unless stated otherwise. You may browse and use the site for personal, informational, and booking-related purposes only.",
      "You may not copy, republish, scrape, reproduce, or commercially exploit the website or its content without prior written permission, except where allowed by law.",
    ],
  },
  {
    title: "Third-Party Services and Links",
    paragraphs: [
      "The website may include third-party tools, maps, analytics services, payment providers, or external links. BlackRide is not responsible for the independent content, privacy practices, or availability of third-party websites or services.",
    ],
  },
  {
    title: "Governing Law",
    paragraphs: [
      "These Terms & Conditions are intended to be interpreted in line with applicable laws and regulations relevant to BlackRide's operations in Saudi Arabia, unless another mandatory legal framework applies. Any disputes should first be raised directly with BlackRide so that the parties can attempt a practical resolution in good faith.",
    ],
  },
  {
    title: "Updates to These Terms",
    paragraphs: [
      "BlackRide may revise these Terms & Conditions as the website, booking workflow, payment process, or operations model evolves. The latest version will be published on this page with an updated effective date.",
    ],
  },
];

export default function TermsConditions() {
  return (
    <LegalPage
      title="Terms & Conditions"
      description="BlackRide Terms & Conditions covering quotes, bookings, deposits, chauffeur services, cancellations, guest responsibilities, and website use in Saudi Arabia."
      canonical="https://getblackride.com/terms-and-conditions"
      updatedLabel="Effective date: 14 May 2026"
      intro="These Terms & Conditions explain how BlackRide handles chauffeur quotes, bookings, confirmations, payments, cancellations, vehicle allocation, and guest responsibilities for premium private transport in Saudi Arabia."
      sections={sections}
    />
  );
}
